import * as DM from '@radix-ui/react-dropdown-menu'
import { clsx } from 'clsx'
import type { ReactNode } from 'react'

export function Menu({ trigger, children, align = 'end' }: { trigger: ReactNode; children: ReactNode; align?: 'start' | 'end' }) {
  return (
    <DM.Root modal={false}>
      <DM.Trigger asChild>{trigger}</DM.Trigger>
      <DM.Portal>
        <DM.Content
          align={align}
          sideOffset={6}
          collisionPadding={12}
          className="z-50 min-w-48 rounded-xl border border-border bg-surface-2 p-1 shadow-xl shadow-black/40"
        >
          {children}
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  )
}

export function MenuItem({
  children,
  icon,
  onSelect,
  danger,
  disabled,
}: {
  children: ReactNode
  icon?: ReactNode
  onSelect: () => void
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <DM.Item
      disabled={disabled}
      onSelect={onSelect}
      className={clsx(
        'flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm outline-none select-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-surface-3',
        danger ? 'text-danger' : 'text-text',
      )}
    >
      {icon && <span className="flex size-4 items-center justify-center text-muted [&>svg]:size-4">{icon}</span>}
      {children}
    </DM.Item>
  )
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DM.Label className="px-2.5 pt-2 pb-1 text-xs font-medium tracking-wide text-faint uppercase">{children}</DM.Label>
}

export function MenuSeparator() {
  return <DM.Separator className="my-1 h-px bg-border" />
}

export function MenuRadio<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: string }[]
}) {
  return (
    <DM.RadioGroup value={value} onValueChange={(v) => onChange(v as T)}>
      {options.map((o) => (
        <DM.RadioItem
          key={o.value}
          value={o.value}
          className="relative flex cursor-pointer items-center rounded-lg py-2 pr-2.5 pl-8 text-sm outline-none select-none data-[highlighted]:bg-surface-3"
        >
          <DM.ItemIndicator className="absolute left-3 size-1.5 rounded-full bg-accent" />
          {o.label}
        </DM.RadioItem>
      ))}
    </DM.RadioGroup>
  )
}
